// Blob URLs for a handful of auth-gated images at once.
//
// `useAuthedImageUrl` resolves one URL and cannot be called in a loop, and the
// star view needs a centre plus five thumbnails as blob URLs (the SVG `<image>`
// cannot send the Bearer header). So this fetches a whole small set in one
// effect and hands back a map, keyed by the original path.
import { useEffect, useState } from 'react'
import { apiClient } from '@/services/api/client'

/** Resolved blob URLs, keyed by the input path. Missing keys are still loading. */
export type AuthedImageUrls = Record<string, string>

/**
 * Fetch every non-null URL of *urls* and return the blob URLs. Meant for short
 * lists (the star view's six images); every object URL is revoked when the set
 * changes or the component unmounts.
 */
export function useAuthedImageUrls(urls: (string | null | undefined)[]): AuthedImageUrls {
  // The dependency is the set of paths, not the array identity the caller
  // rebuilds on every render.
  const key = urls.filter((url): url is string => Boolean(url)).join('\n')
  const [resolved, setResolved] = useState<AuthedImageUrls>({})

  useEffect(() => {
    const wanted = key ? key.split('\n') : []
    if (wanted.length === 0) {
      setResolved({})
      return
    }
    const created: string[] = []
    let cancelled = false

    const load = async () => {
      const token = apiClient.getToken()
      const entries = await Promise.all(
        wanted.map(async url => {
          try {
            const response = await fetch(url, {
              credentials: 'include',
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            })
            if (!response.ok) return null
            const objectUrl = URL.createObjectURL(await response.blob())
            created.push(objectUrl)
            return [url, objectUrl] as const
          } catch {
            return null
          }
        })
      )
      if (cancelled) return
      setResolved(
        Object.fromEntries(entries.filter((entry): entry is [string, string] => entry !== null))
      )
    }

    void load()
    return () => {
      cancelled = true
      for (const objectUrl of created) URL.revokeObjectURL(objectUrl)
    }
  }, [key])

  return resolved
}
